import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import MenuItem from '@material-ui/core/MenuItem'
import Typography from '@material-ui/core/Typography'
import ActionsMenu from 'components/actions-menu'
import {
  DetailField,
  DetailSection,
} from 'pages/pga-coach/students/student-show-page/details-components/content-box'
import RepeatIcon from 'components/material-symbol/icons/Repeat'
import EventAvailableIcon from 'components/material-symbol/icons/EventAvailable'
import CalendarTodayIcon from 'components/material-symbol/icons/CalendarToday'
import SchoolIcon from 'components/material-symbol/icons/School'
import AttachMoneyIcon from 'components/material-symbol/icons/AttachMoney'
import ReceiptIcon from 'components/material-symbol/icons/Receipt'
import MoreHoriz from 'components/material-symbol/icons/MoreHoriz'
import ScheduleIcon from 'components/material-symbol/icons/Schedule'
import CloseIcon from 'components/material-symbol/icons/Close'
import { useAuth } from 'lib/auth'
import { useSnackbar, useSnackbarSuccess, useSnackbarError } from 'lib/snackbar'
import RetentionStatusChip from './retention-status-chip'
import { formatCadence, formatCents, formatLessonDate } from './format'
import { SNOOZE_DAYS } from './constants'
import {
  useDismissStudentInsight,
  useSnoozeStudentInsight,
  useStudentInsight,
} from './api-hooks'

const useStyles = makeStyles(theme => ({
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
  },
  summary: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  reason: {
    fontSize: '0.9375rem',
    fontWeight: 500,
    lineHeight: 1.4,
    color: '#172024',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexShrink: 0,
  },
  menuButton: {
    width: 32,
    height: 32,
    padding: 6,
    borderRadius: 8,
    color: '#6B7C87',
  },
  menuIcon: {
    minWidth: 36,
  },
  metrics: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    columnGap: theme.spacing(3),
    rowGap: theme.spacing(1.5),
    '& > *': {
      alignItems: 'flex-start',
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
}))

// canManage is false for staff sessions that hide sensitive information,
// matching SensitiveInfoWrapper around the student's Message buttons.
export function InsightsCardView({ insight, canManage, onSnooze, onDismiss }) {
  const classes = useStyles()

  return (
    <DetailSection
      title="Booking insights"
      layout="plain"
      headerAction={<RetentionStatusChip status={insight.status} />}
    >
      <Box className={classes.body}>
        <Box className={classes.summary}>
          <Typography className={classes.reason}>{insight.reason}</Typography>
          <Box className={classes.actions}>
            {canManage && insight.needsAttention ? (
              <ActionsMenu
                ariaId="student-insight-actions"
                aria-label="More follow-up options"
                buttonContent={<MoreHoriz fontSize="small" />}
                className={classes.menuButton}
                color="default"
              >
                <MenuItem onClick={onSnooze}>
                  <ListItemIcon className={classes.menuIcon}>
                    <ScheduleIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Remind me in 2 weeks" />
                </MenuItem>
                <MenuItem onClick={onDismiss}>
                  <ListItemIcon className={classes.menuIcon}>
                    <CloseIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="No follow-up needed" />
                </MenuItem>
              </ActionsMenu>
            ) : null}
          </Box>
        </Box>

        <Box className={classes.metrics}>
          <DetailField
            icon={RepeatIcon}
            label="Usually books"
            value={formatCadence(insight.typicalCadenceDays)}
          />
          <DetailField
            icon={EventAvailableIcon}
            label="Last lesson"
            value={formatLessonDate(insight.lastLessonAt)}
          />
          <DetailField
            icon={CalendarTodayIcon}
            label="Next lesson"
            value={formatLessonDate(insight.nextLessonAt)}
          />
          <DetailField
            icon={SchoolIcon}
            label="Lessons"
            value={insight.completedCount}
            hint={
              insight.canceledCount
                ? `${insight.canceledCount} canceled`
                : insight.firstLessonAt &&
                `Since ${formatLessonDate(insight.firstLessonAt)}`
            }
          />
          <DetailField
            icon={AttachMoneyIcon}
            label="Lesson revenue"
            value={formatCents(insight.lifetimeValueInCents)}
            hint="Net of refunds"
          />
          <DetailField
            icon={ReceiptIcon}
            label="Average booking"
            value={formatCents(insight.averageBookingValueInCents)}
          />
        </Box>
      </Box>
    </DetailSection>
  )
}

InsightsCardView.propTypes = {
  insight: PropTypes.shape({
    status: PropTypes.string.isRequired,
    reason: PropTypes.string.isRequired,
    needsAttention: PropTypes.bool.isRequired,
    typicalCadenceDays: PropTypes.number,
    firstLessonAt: PropTypes.string,
    lastLessonAt: PropTypes.string,
    nextLessonAt: PropTypes.string,
    completedCount: PropTypes.number.isRequired,
    canceledCount: PropTypes.number.isRequired,
    lifetimeValueInCents: PropTypes.number.isRequired,
    averageBookingValueInCents: PropTypes.number,
  }).isRequired,
  canManage: PropTypes.bool,
  onSnooze: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
}

InsightsCardView.defaultProps = {
  canManage: true,
}

export default function StudentInsightsCard({ studentId }) {
  const { data, refetch } = useStudentInsight(studentId)
  const { user } = useAuth()
  const [snooze] = useSnoozeStudentInsight()
  const [dismiss] = useDismissStudentInsight()
  const { enqueueSnackbar } = useSnackbar()
  const snackbarSuccess = useSnackbarSuccess(enqueueSnackbar)
  const snackbarError = useSnackbarError(enqueueSnackbar)

  const insight = data?.student?.insights
  if (!insight) return null

  const runQueueAction = async (mutate, variables, key) => {
    try {
      const { data: result } = await mutate({ variables })
      const response = result?.[key]
      if (!response?.success) throw new Error(response?.message)
      snackbarSuccess(response.message)
      refetch()
    } catch (e) {
      snackbarError(e.message || 'Something went wrong. Try again.')
    }
  }

  return (
    <InsightsCardView
      insight={insight}
      canManage={!user?.hideSensitiveInformation}
      onSnooze={() =>
        runQueueAction(
          snooze,
          { studentId, days: SNOOZE_DAYS },
          'snoozeStudentInsight',
        )
      }
      onDismiss={() =>
        runQueueAction(dismiss, { studentId }, 'dismissStudentInsight')
      }
    />
  )
}

StudentInsightsCard.propTypes = {
  studentId: PropTypes.string.isRequired,
}
